import { toImageData } from "./rasterImageData"

const hexToRgb = (hex: string): [number, number, number] => {
    const value = hex.replace("#", "")
    return [
        parseInt(value.slice(0, 2), 16),
        parseInt(value.slice(2, 4), 16),
        parseInt(value.slice(4, 6), 16),
    ]
}

// Lightness is discarded - the transform substitutes each pixel's own gray value for it.
const rgbToHueSaturation = (r: number, g: number, b: number): [number, number] => {
    const rf = r / 255
    const gf = g / 255
    const bf = b / 255
    const max = Math.max(rf, gf, bf)
    const min = Math.min(rf, gf, bf)
    const delta = max - min
    if (delta === 0) return [0, 0]
    const lightness = (max + min) / 2
    const saturation = delta / (1 - Math.abs(2 * lightness - 1))
    let hue: number
    if (max === rf) hue = ((gf - bf) / delta) % 6
    else if (max === gf) hue = (bf - rf) / delta + 2
    else hue = (rf - gf) / delta + 4
    hue *= 60
    if (hue < 0) hue += 360
    return [hue, saturation]
}

const hslToRgb = (h: number, s: number, l: number): [number, number, number] => {
    const c = (1 - Math.abs(2 * l - 1)) * s
    const x = c * (1 - Math.abs(((h / 60) % 2) - 1))
    const m = l - c / 2
    let [r, g, b] = [0, 0, 0]
    if (h < 60) [r, g, b] = [c, x, 0]
    else if (h < 120) [r, g, b] = [x, c, 0]
    else if (h < 180) [r, g, b] = [0, c, x]
    else if (h < 240) [r, g, b] = [0, x, c]
    else if (h < 300) [r, g, b] = [x, 0, c]
    else [r, g, b] = [c, 0, x]
    return [(r + m) * 255, (g + m) * 255, (b + m) * 255]
}

// Matches QGIS's raster "Colorize" draw effect (singleband gray + Hue colorize + strength).
export function createHueColorizeTransform(hex: string, strength: number) {
    const [hr, hg, hb] = hexToRgb(hex)
    const [hue, saturation] = rgbToHueSaturation(hr, hg, hb)
    // renderSubLayers re-invokes this per tile on every comparisonClip threshold change, not just on new tile data.
    const cache = new WeakMap<ImageBitmap | ImageData, ImageData>()

    return (source: ImageBitmap | ImageData): ImageData => {
        const cached = cache.get(source)
        if (cached) return cached

        const { data, width, height } = toImageData(source)
        const out = new ImageData(new Uint8ClampedArray(data), width, height)
        for (let i = 0; i < out.data.length; i += 4) {
            // qGray() weights, matching QGIS's own grayscale conversion
            const gray = (out.data[i] * 11 + out.data[i + 1] * 16 + out.data[i + 2] * 5) / 32 / 255
            const [cr, cg, cb] = hslToRgb(hue, saturation, gray)
            out.data[i] = out.data[i] * (1 - strength) + cr * strength
            out.data[i + 1] = out.data[i + 1] * (1 - strength) + cg * strength
            out.data[i + 2] = out.data[i + 2] * (1 - strength) + cb * strength
        }
        cache.set(source, out)
        return out
    }
}
